import { useMemo, useState } from 'react'
import { ChevronUp, ChevronDown, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { EmptyState } from './EmptyState'

export interface DataTableColumn<T> {
  key: keyof T & string
  header: string
  render?: (row: T) => React.ReactNode
  sortable?: boolean
  filterable?: boolean
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  pageSize?: number
  /** Show the built-in client-side search input. Default true. Set false when the page owns a backend-backed search elsewhere. */
  searchable?: boolean
  searchPlaceholder?: string
  emptyTitle?: string
  emptyDescription?: string
  className?: string
}

type SortState<T> = { key: keyof T & string; dir: 'asc' | 'desc' } | null

export function DataTable<T extends object>({
  columns,
  rows,
  pageSize = 20,
  searchable = true,
  searchPlaceholder = 'Filter…',
  emptyTitle = 'No results',
  emptyDescription,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState<T>>(null)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)

  const filterableKeys = useMemo(
    () => columns.filter((c) => c.filterable !== false).map((c) => c.key),
    [columns],
  )

  const filtered = useMemo(() => {
    if (!query) return rows
    const q = query.toLowerCase()
    return rows.filter((r) => {
      const rec = r as Record<string, unknown>
      return filterableKeys.some((k) => String(rec[k] ?? '').toLowerCase().includes(q))
    })
  }, [rows, query, filterableKeys])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const arr = [...filtered]
    arr.sort((a, b) => {
      const av = (a as Record<string, unknown>)[sort.key]
      const bv = (b as Record<string, unknown>)[sort.key]
      if (av === bv) return 0
      const cmp = (av as never) < (bv as never) ? -1 : 1
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sort])

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize)

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {searchable && (
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPage(0)
            }}
            className="max-w-xs"
            aria-label={searchPlaceholder}
          />
        </div>
      )}

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {columns.map((col) => {
                const sortable = col.sortable !== false
                const active = sort?.key === col.key
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      'px-3 py-2 text-left font-semibold',
                      sortable && 'cursor-pointer select-none',
                    )}
                    aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    onClick={
                      sortable
                        ? () => {
                            setSort((prev) => {
                              if (!prev || prev.key !== col.key) return { key: col.key, dir: 'asc' }
                              return { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                            })
                          }
                        : undefined
                    }
                  >
                    <span className="flex items-center gap-1">
                      {col.header}
                      {active &&
                        (sort!.dir === 'asc' ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        ))}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-8">
                  <EmptyState title={emptyTitle} description={emptyDescription} />
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => (
                <tr key={i} className="border-t hover:bg-accent/30">
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2">
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Page {page + 1} of {pageCount}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
